// Who owns the keyboard: a text field the player is typing in, a UI layer that
// has declared itself modal, or the game.
//
// WHY THIS IS ITS OWN FILE. input.js cannot be loaded by plain Node (it is
// written against the DOM and lives inside the bundle), and the join screen
// cannot be clicked by a harness without a browser — and a browser measurement
// is banned on this box. The decision itself is pure, so it lives here as .mjs
// and tools/uikeys.mjs proves every case in Node. Getting this wrong locks 80
// players out of their own controls, so it gets a test, not a code review.
//
// THE RULE, stated once: the game receives a keystroke unless a modal UI layer
// is open, or the focused element is a text-entry control. Nothing else gates
// it — in particular a focused BUTTON does NOT, because a stray focused button
// (the PLAY button after a click, a future settings toggle) would otherwise
// swallow Space and jump forever, which is exactly the failure this file
// exists to prevent. Layers that need the whole keyboard say so explicitly
// with setKeyboardModal(), which is checked first and clears the held keys.

// A <textarea> always takes the keyboard. An <input> takes it only when it has
// a CARET — which is the whole question, because the blanket "every <input>"
// rule locked the player out of the game: the F2 animation panel has a `freeze`
// checkbox (animdebug.js:123), clicking it leaves focus on an
// <input type=checkbox>, and that checkbox does not stopPropagation the way the
// search field does — so every later keydown reached the gate, was read as text
// entry, and WASD, Space, F/G, Tab, F1, F2 and F4 all went dead. Including F2,
// the panel's own documented close key, so there was no way back out except
// clicking the canvas. A checkbox, radio, slider or colour swatch has no caret
// and therefore no claim on a game key; a type we do not recognise is treated
// as text, which is the safe default for the next <input type="search">.
const NON_TEXT_INPUT = new Set(['checkbox', 'radio', 'range', 'button', 'submit',
  'reset', 'image', 'color', 'file']);

// `el` is duck-typed on purpose: a real DOM element in the bundle, a plain
// object in the Node harness. Only tagName / type / isContentEditable are read.
export function isTextEntry(el) {
  if (!el) return false;
  if (el.isContentEditable) return true;
  if (el.tagName === 'TEXTAREA') return true;
  // an <input> with no `type` is a text field (the HTML default), which is what
  // the join screen's name field and the harness fixture both look like
  if (el.tagName === 'INPUT') return !NON_TEXT_INPUT.has(String(el.type || 'text').toLowerCase());
  return false;
}

// Returns true when the keystroke belongs to the UI and must NOT reach the
// game. Both `target` and `activeElement` are consulted: for a real keystroke
// they are the same node, but a synthetic event dispatched on document/window
// (the capture pipeline does this) has target = document, in which case
// activeElement is the only authority on where the caret is.
export function uiOwnsKey({ target, activeElement, modalDepth = 0 } = {}) {
  if (modalDepth > 0) return true;
  return isTextEntry(target) || isTextEntry(activeElement);
}
